import { generateDownloadAnalyticsData } from 'helpers/generateDownloadAnalyticsData';
import { t } from 'i18next';
import type { Middleware } from 'redux';

import { sendAnalyticEvent } from 'data/actions/analytics';
import { setWillDownloadBySSE } from 'data/actions/documents';
import { setSseFileToDownload, setSseMessage } from 'data/actions/events';
import { toggleModal } from 'data/actions/modals';
import { userIdSelector } from 'data/selectors/user';

import showToast from 'utils/toast';

import { MAX_RECONNECT_ATTEMPTS, RECONNECT_DELAY } from 'ts/constants/general';
import { ECreationTypes } from 'ts/enums/creationTypes';
import { EModalTypes } from 'ts/enums/modal.types';
import type { IMessageEvent } from 'ts/interfaces/common/events';
import type { IReduxAction } from 'ts/interfaces/redux/redux.action';

const HANDLED_CREATION_TYPES = [
  ECreationTypes.CONVERTER,
  ECreationTypes.OPTIMIZATOR,
  ECreationTypes.OCR,
  ECreationTypes.MERGER,
  ECreationTypes.TRANSLATE
];

const SSE_URL = process.env.REACT_APP_SSE_URL!;

const eventsMiddleware = (): Middleware => {
  let eventSource: EventSource | null = null;
  let currentUserId: string | null = null;
  let reconnectAttempts = 0;
  return (store) => (next) => (action: IReduxAction) => {
    const userId = userIdSelector(store.getState());
    if (currentUserId !== userId) {
      if (eventSource) {
        eventSource.close();
      }
      eventSource = null;
      currentUserId = userId;
    }

    const dispatchDownloadError = () => {
      store.dispatch(
        sendAnalyticEvent({
          event: 'file_download_status',
          data: generateDownloadAnalyticsData({ success: false, isManual: false })
        })
      );
      store.dispatch(toggleModal({ type: EModalTypes.FILE_UPLOAD_ERROR, visible: true }));
    };

    const isCreationType = (value: string): value is ECreationTypes =>
      Object.values(ECreationTypes).includes(value as ECreationTypes);

    if (!eventSource && userId) {
      const onMessageHandler = ({ data }: MessageEvent) => {
        try {
          const message = JSON.parse(data) as IMessageEvent;

          store.dispatch(setSseMessage(message));

          if (message.type === 'one-time-purchase-fail') {
            store.dispatch(
              toggleModal({
                type: EModalTypes.PAYMENT_ERROR,
                visible: true,
                options: {
                  errorCode: message.data?.code,
                  errorMessage: message.data?.recommended_message_for_user,
                  showTryAgainButton: false
                }
              })
            );
            return;
          }

          if (message.type === 'file-processing-result') {
            store.dispatch(setSseFileToDownload(message));
            return;
          }

          if (
            message.type === 'one-time-purchase-success' &&
            (window.location.pathname.includes('payment-success') ||
              window.location.pathname.includes('account-settings'))
          ) {
            store.dispatch(toggleModal({ type: EModalTypes.UPSALE_SUCCESS, visible: true }));
            return;
          }

          if (message?.data?.processing_status === 'FAILED') {
            dispatchDownloadError();
            return;
          }

          if (!message?.data?.creation_type) return;

          store.dispatch(setSseMessage(message));

          if (
            isCreationType(message.data?.creation_type) &&
            HANDLED_CREATION_TYPES.includes(message.data.creation_type) &&
            message.data.id
          ) {
            store.dispatch(setWillDownloadBySSE(false));
          }
        } catch (err) {
          dispatchDownloadError();
          throw new Error(`${err}`);
        }
      };

      const handleError = () => {
        if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts++;
          eventSource?.close();
          setTimeout(() => {
            eventSource = new EventSource(SSE_URL, { withCredentials: true });
            eventSource.onopen = () => {
              reconnectAttempts = 0;
            };
            eventSource.onerror = handleError;
            eventSource.onmessage = onMessageHandler;
          }, RECONNECT_DELAY);
        } else {
          showToast('error', t('global.connection_error'), 5000, 'top-center');
        }
      };

      eventSource = new EventSource(SSE_URL, { withCredentials: true });
      eventSource.onopen = () => {
        reconnectAttempts = 0;
      };
      eventSource.onerror = handleError;
      eventSource.onmessage = onMessageHandler;
    }

    return next(action);
  };
};

export default eventsMiddleware;
